import { authConfig } from '@/features/auth/config';

type ToolAccessReserveResult = {
  allowed: boolean;
  toolName: string;
  quotaMetric: string | null;
  reservedUnits: number;
  remainingBeforeExecution: number | null;
  billingMode: string;
  planCode: string;
  planName: string;
  subscriptionStatus: string;
};

type ToolAccessErrorResponse = {
  message: string;
  code?: string;
};

export class FastcutToolAccessError extends Error {
  constructor(
    public readonly code: string | undefined,
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'FastcutToolAccessError';
  }
}

async function requestJson<T>(
  url: string,
  body: unknown,
): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const payload = await response.json().catch(() => null) as T | ToolAccessErrorResponse | null;

  if (!response.ok) {
    const errorPayload = payload as ToolAccessErrorResponse | null;
    throw new FastcutToolAccessError(
      errorPayload?.code,
      errorPayload?.message ?? `工具权限检查失败 (${response.status})`,
      response.status,
    );
  }

  return payload as T;
}

export async function reserveToolAccess(input: {
  toolName: string;
  requestedUnits?: number | null;
}): Promise<ToolAccessReserveResult> {
  return requestJson<ToolAccessReserveResult>(authConfig.toolAccessReserveEndpoint, {
    toolName: input.toolName,
    requestedUnits: input.requestedUnits ?? undefined,
  });
}

export async function commitToolAccess(input: {
  toolName: string;
  quotaMetric?: string | null;
  reservedUnits: number;
}): Promise<void> {
  await requestJson<{ committed: boolean }>(authConfig.toolAccessCommitEndpoint, {
    toolName: input.toolName,
    quotaMetric: input.quotaMetric ?? undefined,
    reservedUnits: input.reservedUnits,
  });
}

export async function releaseToolAccess(input: {
  toolName: string;
  quotaMetric?: string | null;
  reservedUnits: number;
}): Promise<void> {
  await requestJson<{ released: boolean }>(authConfig.toolAccessReleaseEndpoint, {
    toolName: input.toolName,
    quotaMetric: input.quotaMetric ?? undefined,
    reservedUnits: input.reservedUnits,
  });
}

export function estimateTtsProcessingSeconds(text: string) {
  const normalized = text.replace(/\s+/g, ' ').trim()
  if (!normalized) {
    return 1
  }

  return Math.max(1, Math.ceil(normalized.length / 4))
}
